/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Input as InputIcon, ExpandMore, ExpandLess } from '@material-ui/icons';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import {
  InstanceStatusFields,
  InstanceStatusResultsFields,
} from 'modules/monitor/interfaces';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import { getInstanceStatusRequest } from 'modules/monitor/actions';
import { cyrb53Hash } from 'utils/helpers';
import { getCompanyName } from 'utils/tsHelpers';
import { useTableFilters } from 'hooks/useTableFilters';
import styles from './styles';

const initialQuery = {
  offset: 0,
  limit: 50,
  status: '',
  period: '',
  search: '',
};

interface Props {
  isLoading: boolean;
  instanceStatusData: InstanceStatusFields;
  modalClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const InstanceStatusTable = ({
  isLoading,
  instanceStatusData,
  modalClose,
  selectCompany,
  selectPartner,
}: Props) => {
  const [collapseTable, setCollapseTable] = React.useState(true);

  const headers = [
    {
      key: '',
      i18n: i18n.t('ACCESS_KEY'),
      centerTitle: true,
    },
    {
      key: 'partner',
      i18n: i18n.t('MONITOR.PARTNER_NAME'),
      order: false,
    },
    {
      key: 'environment',
      i18n: i18n.t('MONITOR.ENVIRONMENT_NAME'),
      order: false,
    },
    {
      key: 'flavor',
      i18n: i18n.t('MONITOR.FLAVOR'),
      order: false,
    },
    {
      key: 'zone',
      i18n: i18n.t('MONITOR.ZONE'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'count',
      i18n: i18n.t('MONITOR.COUNT'),
      order: false,
      centerTitle: true,
    },
  ];

  const useStyles = makeStyles(styles());
  const classes = useStyles();
  const history = useHistory();

  const { handleLimitChange, handlePageChange } = useTableFilters(initialQuery);

  const redirectToContext = (item: InstanceStatusResultsFields) => {
    selectCompany({
      id: item?.company.uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      id: item?.partner.uuid,
      name: item?.partner.name,
      shortName: item?.partner.name,
    });
    history.push(`/environments/${item?.environment.uuid}/instances`);
  };

  return (
    <Grid className={classes.table}>
      <Grid
        className={classes.titleBackground}
        onClick={() => setCollapseTable(!collapseTable)}
      >
        <Typography className={classes.tableTitle}>
          {i18n.t('MONITOR.INSTANCES_STATUS_TITLE')}
          {collapseTable ? <ExpandLess /> : <ExpandMore />}
        </Typography>
      </Grid>
      {collapseTable && (
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell key={item?.key} centerTitle={item?.centerTitle}>
                  {item?.i18n}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: InstanceStatusResultsFields, index) => (
            <TableRow key={cyrb53Hash(index)}>
              <TableCell centerTitle textAlignCenter>
                <IconButton
                  className={classes.accessButton}
                  onClick={() => {
                    modalClose();
                    redirectToContext(item);
                  }}
                >
                  <InputIcon />
                </IconButton>
              </TableCell>
              <TableCell className={classes.text}>
                <Tooltip title={item?.partner.name}>
                  <Typography>{item?.partner.name}</Typography>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.text}>
                <Tooltip title={item?.environment.name}>
                  <Typography>{item?.environment.name}</Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <span
                  style={{
                    display: 'flex',
                    paddingRight: 10,
                  }}
                >
                  {item?.flavor}
                </span>
              </TableCell>
              <TableCell centerTitle>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingRight: 10,
                  }}
                >
                  {item?.zone}
                </span>
              </TableCell>
              <TableCell>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingRight: 10,
                  }}
                >
                  {item?.status_quantity}
                </span>
              </TableCell>
            </TableRow>
          )}
          data={instanceStatusData}
          isLoading={isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={
            <Typography className={classes.notFoundText}>
              {i18n.t('MONITOR.NO_RESULTS_INSTANCES')}
            </Typography>
          }
          notFooter
        />
      )}
    </Grid>
  );
};

const mapStateToProps = ({ dashboard: { isLoading } }: ApplicationState) => ({
  isLoading,
});

export default connect(mapStateToProps, {
  getInstanceStatusRequest,
  modalClose,
  selectCompany,
  selectPartner,
})(InstanceStatusTable);
